import type { SessionScope } from '@/types/auth';
import { isAggregateScope } from '@/lib/session-scope';
import type { SettingsSectionId } from './settings-sections';

/**
 * The settings-surface session modes:
 * - master: brand owner — full edit (today's behavior).
 * - ownLine: independent per-sucursal line — inherits master config with
 *   per-field overrides on General/WhatsApp; per-line resources (flows) editable.
 * - sharedLine: sucursal riding the master's line — pure view-only.
 * - aggregate: parent master client spanning many brands — configuration
 *   belongs to a single brand, so every section is hidden and the route renders
 *   the "log in at that brand's level" banner instead of this page.
 */
export type SettingsSessionMode = 'master' | 'ownLine' | 'sharedLine' | 'aggregate';

export type TabAccess = 'edit' | 'override' | 'viewOnly' | 'locked' | 'hidden';

export function getSessionMode(scope: SessionScope | null | undefined): SettingsSessionMode {
  if (isAggregateScope(scope)) return 'aggregate';
  if (!scope?.readOnly) return 'master';
  return scope.hasOwnLine ? 'ownLine' : 'sharedLine';
}

/**
 * Single source of truth for what each settings section allows per session mode. The matrix
 * values for the sections that existed as tabs are unchanged; `notifications` follows
 * `payments` (brand-wide, master-owned), and `external` is master-only — a sucursal
 * has no connector of its own.
 * `delivery` (the rider provider) follows `pos`: a brand-level connection, master-owned.
 *
 * The UI is a convenience: the backend 403s a scoped session's writes regardless (OD-11). Keep
 * the two in agreement.
 */
const SECTION_ACCESS_BY_MODE: Record<SettingsSessionMode, Record<SettingsSectionId, TabAccess>> = {
  master: {
    general: 'edit',
    catalog: 'edit',
    discounts: 'edit',
    locations: 'edit',
    payments: 'edit',
    pos: 'edit',
    delivery: 'edit',
    policies: 'edit',
    notifications: 'edit',
    whatsapp: 'edit',
    'whatsapp/flows': 'edit',
    external: 'edit',
  },
  ownLine: {
    general: 'override',
    catalog: 'viewOnly',
    // Coupons and promotions are brand-wide (the backend 403s a sucursal's writes), but a
    // sucursal still has to be able to read what is running — hence view-only, not locked.
    discounts: 'viewOnly',
    locations: 'locked',
    payments: 'viewOnly',
    pos: 'locked',
    delivery: 'locked',
    policies: 'hidden',
    notifications: 'viewOnly',
    whatsapp: 'override',
    'whatsapp/flows': 'edit', // flow installation is per-line
    external: 'hidden',
  },
  sharedLine: {
    general: 'viewOnly',
    catalog: 'viewOnly',
    discounts: 'viewOnly',
    locations: 'locked',
    payments: 'viewOnly',
    pos: 'locked',
    delivery: 'locked',
    policies: 'hidden',
    notifications: 'viewOnly',
    whatsapp: 'viewOnly',
    'whatsapp/flows': 'locked', // no own line to install flows on
    external: 'hidden',
  },
  aggregate: {
    general: 'hidden',
    catalog: 'hidden',
    discounts: 'hidden',
    locations: 'hidden',
    payments: 'hidden',
    pos: 'hidden',
    delivery: 'hidden',
    policies: 'hidden',
    notifications: 'hidden',
    whatsapp: 'hidden',
    'whatsapp/flows': 'hidden',
    external: 'hidden',
  },
};

export function getSectionAccess(mode: SettingsSessionMode): Record<SettingsSectionId, TabAccess> {
  return SECTION_ACCESS_BY_MODE[mode];
}
